import { useState, useRef, useEffect, useContext } from "react";

// material-ui
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  ClickAwayListener,
  Divider,
  Paper,
  Popper,
  Stack,
  Typography,
} from "@mui/material";

// assets
import { IconChevronDown, IconLogout } from "@tabler/icons";
import Transitions from "../../../components/Transition";
import { UserContext } from "src/state/userState";

// ==============================|| PROFILE MENU ||============================== //

/**
 * Who is signed in, and the way out.
 *
 * It used to be a solid black 48px pill in the top-right corner - the heaviest
 * thing on any screen, for a control nobody presses twice a day. It is a quiet
 * button now; the only colour is the avatar.
 */
const ProfileSection = () => {
  const { user } = useContext(UserContext);

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const prevOpen = useRef(open);

  const handleToggle = () => setOpen((previous) => !previous);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const initial = (user?.name || "?")[0].toUpperCase();

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        onClick={handleToggle}
        aria-controls={open ? "profile-menu" : undefined}
        aria-haspopup="true"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: 40,
          pl: 0.5,
          pr: 1.25,
          borderRadius: 2,
          border: "1px solid",
          borderColor: open ? "grey.300" : "transparent",
          backgroundColor: open ? "grey.50" : "transparent",
          transition: "background-color .15s ease, border-color .15s ease",
          "&:hover": { backgroundColor: "grey.50", borderColor: "grey.200" },
        }}
      >
        <Avatar
          variant="rounded"
          sx={{
            width: 30,
            height: 30,
            fontSize: "0.8125rem",
            fontWeight: 600,
            borderRadius: 1.5,
            bgcolor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          {initial}
        </Avatar>
        <Typography
          variant="subtitle2"
          sx={{ color: "text.primary", display: { xs: "none", sm: "block" } }}
        >
          {user?.name}
        </Typography>
        <IconChevronDown
          stroke={2}
          size="1rem"
          style={{
            color: "#667085",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s ease",
          }}
        />
      </ButtonBase>

      <Popper
        id="profile-menu"
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: "offset", options: { offset: [0, 8] } }] }}
        style={{ zIndex: 1200 }}
      >
        {({ TransitionProps }) => (
          <Transitions
            in={open}
            {...TransitionProps}
          >
            <Paper
              elevation={8}
              sx={{ borderRadius: 2.5, border: "1px solid", borderColor: "divider", minWidth: 244 }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  <Box sx={{ px: 2, py: 1.75 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600 }}
                    >
                      {user?.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", wordBreak: "break-all" }}
                    >
                      {user?.email}
                    </Typography>
                    {user?.role && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "inline-block",
                          mt: 1,
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          bgcolor: "primary.lighter",
                          color: "primary.dark",
                          fontWeight: 600,
                        }}
                      >
                        {user.role}
                      </Typography>
                    )}
                  </Box>

                  <Divider />

                  <Box sx={{ p: 1 }}>
                    <Button
                      fullWidth
                      variant="text"
                      startIcon={<IconLogout size="1.05rem" />}
                      onClick={window.handleLogout}
                      sx={{ justifyContent: "flex-start", color: "text.primary" }}
                    >
                      Log out
                    </Button>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default ProfileSection;
